import type {
  CMSFaq,
  CMSFaqCategories,
  CMSFaqCategory,
  CMSFaqItem,
  CMSRichText,
  CMSSys,
} from '../types';
import { adaptLocalizationConfiguration } from './ug/country';

type AdaptSysFn = (sys: unknown) => CMSSys;

type FaqListCollection = {
  items?: Array<Record<string, unknown> | null> | null;
} | null;

function adaptFaqAnswer(
  faqAnswer: { json?: unknown; links?: CMSRichText['links'] } | string | null | undefined,
  fallbackAnswer?: string | null
): CMSRichText | string {
  if (typeof faqAnswer === 'string') {
    return faqAnswer;
  }

  if (faqAnswer?.json !== undefined) {
    return {
      json: faqAnswer.json,
      links: faqAnswer.links,
    };
  }

  return fallbackAnswer || '';
}

/**
 * Adapt a single FAQ item from Contentful to the domain model.
 */
export function adaptFaqItem(faq: Record<string, unknown>, adaptSys: AdaptSysFn): CMSFaqItem {
  const question = (faq.faqQuestion as string) || (faq.question as string) || '';
  const faqAnswer = faq.faqAnswer as
    | { json?: unknown; links?: CMSRichText['links'] }
    | string
    | null
    | undefined;

  return {
    sys: adaptSys(faq.sys),
    question,
    answer: adaptFaqAnswer(faqAnswer, faq.answer as string | null),
    localizationConfiguration: adaptLocalizationConfiguration(faq.localizationConfiguration),
  };
}

/**
 * Adapt FaqWrap section from Contentful to CMSFaq.
 */
export function adaptFaqWrap(section: Record<string, unknown>, adaptSys: AdaptSysFn): CMSFaq {
  const faqListCollection = section.faqListCollection as FaqListCollection;
  const headline = section.placeholderHeading as {
    text?: string;
  } | null;

  const seenQuestionIds = new Set<string>();
  const faqItems = (faqListCollection?.items || [])
    .filter((item): item is Record<string, unknown> => item != null)
    .filter((item) => {
      const id = adaptSys(item.sys).id;
      if (!id) return true;
      if (seenQuestionIds.has(id)) return false;
      seenQuestionIds.add(id);
      return true;
    })
    .map((item) => adaptFaqItem(item, adaptSys));

  const category: CMSFaqCategory = {
    sys: adaptSys(section.sys),
    name: headline?.text || 'General',
    questions: faqItems,
  };

  return {
    __typename: 'FaqWrap',
    sys: adaptSys(section.sys),
    internalName: (section.internalName as string) || null,
    localizationConfiguration: adaptLocalizationConfiguration(section.localizationConfiguration),
    heading: headline?.text || null,
    categories: faqItems.length > 0 ? [category] : [],
    collapse: (section.collapse as boolean) ?? true,
  };
}

/**
 * Adapt FaqCategories section from Contentful to CMSFaqCategories.
 */
export function adaptFaqCategories(
  section: Record<string, unknown>,
  adaptSys: AdaptSysFn
): CMSFaqCategories {
  const faqWrapsCollection = section.faqWrapsCollection as {
    items?: Array<Record<string, unknown> | null> | null;
  } | null;
  const faqWraps = (faqWrapsCollection?.items || [])
    .filter((item): item is Record<string, unknown> => item != null)
    .map((item) => adaptFaqWrap(item, adaptSys))
    .filter((wrap) => (wrap.categories?.[0]?.questions.length ?? 0) > 0);

  return {
    __typename: 'FaqCategories',
    sys: adaptSys(section.sys),
    internalName: (section.internalName as string) || null,
    categoryLabel: (section.categoryLabel as string) || null,
    faqWraps,
  };
}
